import { clsx } from 'clsx';
import { InputNumber, Popconfirm, Select } from 'antd';
import { Button } from '@mui/material';

import { TableCropRow } from '@network';
import cls from './table-crop.module.css';

type Column = 'year' | 'incomeTotal' | 'profit';
type Row = TableCropRow;
type Header = {key: Column, label: string};

interface Props {
  data: {rows: Row[]} | null,
  onChange: any;
  isFixed?: boolean;
}

const headers: Header[] = [
  { key: 'year', label: 'Year' },
  { key: 'incomeTotal', label: 'Total' },
  { key: 'profit', label: 'Profit' },
];
const defaultData: {headers: Header[], rows: Row[]} = { headers, rows: [] };

const IconDel = () => (
  <svg width="1em" height="1em" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 0C10.866 0 14 3.13401 14 7C14 10.866 10.866 14 7 14C3.13401 14 0 10.866 0 7C0 3.13401 3.13401 0 7 0ZM9.9502 4.29297C9.55967 3.90244 8.92568 3.90244 8.53516 4.29297L7.12109 5.70605L5.70703 4.29297C5.31651 3.90244 4.68349 3.90244 4.29297 4.29297C3.90244 4.68349 3.90244 5.31651 4.29297 5.70703L5.70605 7.12109L4.29297 8.53516C3.90244 8.92568 3.90244 9.55967 4.29297 9.9502C4.68343 10.3404 5.31657 10.3404 5.70703 9.9502L7.12109 8.53516L8.53516 9.9502C8.92568 10.3407 9.55968 10.3407 9.9502 9.9502C10.3407 9.55968 10.3407 8.92568 9.9502 8.53516L8.53516 7.12109L9.9502 5.70703C10.3404 5.31657 10.3404 4.68343 9.9502 4.29297Z" fill="#D32F2F"/>
  </svg>
);

const IconAdd = () => (
  <svg width="1em" height="1em" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C16.4183 0 20 3.58172 20 8V12C20 16.4183 16.4183 20 12 20H8C3.58172 20 6.44277e-08 16.4183 0 12V8C0 3.58172 3.58172 6.44256e-08 8 0H12ZM10 6C9.44772 6 9 6.44772 9 7V9H7C6.44772 9 6 9.44772 6 10C6 10.5523 6.44772 11 7 11H9V13C9 13.5523 9.44772 14 10 14C10.5523 14 11 13.5523 11 13V11H13C13.5523 11 14 10.5523 14 10C14 9.44772 13.5523 9 13 9H11V7C11 6.44772 10.5523 6 10 6Z" fill="#042E6B"/>
  </svg>
);

export const TableCrop = ({ data, onChange, isFixed }: Props) => {
  const fields: Column[] = headers.map(header => header.key);

  const onFieldChange = (val: number | null | string, key: Column, index: number) => {
    if (val === null) return;
    const nextRows = (data || defaultData)
      .rows.map((row, i) => i === index ? { ...row, [key]: val } : row);
    const nextData = { ...data, rows: nextRows };
    onChange(nextData);
  };

  const addRow = () => {
    const newRow: Row = { year: null, incomeTotal: 0, profit: 0 };
    const nextData = data || defaultData;
    nextData.rows = (nextData.rows || []).concat([newRow]);
    onChange(nextData);
  };
  const removeRow = (index: number) => {
    const nextData = data || defaultData;
    nextData.rows = (nextData.rows || []).filter((_, i) => i !== index);
    onChange(nextData);
  };

  const rows = (data || defaultData).rows;
  let incomeTotal = 0;
  let profit = 0;

  for (const row of rows) {
    incomeTotal += +(row.incomeTotal || 0);
    profit += +(row.profit || 0);
  }

  const accumulatedRow: Row = {
    year: 'Accumulated',
    incomeTotal,
    profit,
  };
  const usedYears = rows.map(row => `${row.year}`).filter(Boolean) as string[];

  return (
    <div className={cls.block}>
      <div className={cls.table}>
        <div className={cls.header}>
          <div className={cls.headerRow}>
            <div className={cls.headerCol}>Year</div>
            <div className={cls.headerCol}>Total</div>
            <div className={cls.headerCol}>Profit</div>
          </div>
        </div>
        <div className={cls.body}>
          {rows.map((row, index) => (
            <div className={clsx(cls.row, cls.rowColored)} key={index}>
              {fields.map(field => (
                <div className={cls.col} key={field}>
                  <Input row={row} field={field} onChange={onFieldChange}
                    index={index} isFixed={isFixed} usedYears={usedYears} />
                </div>
              ))}
              {!isFixed && (
                <Popconfirm title="Remove row?" onConfirm={() => removeRow(index)}>
                  <Button color="secondary" variant="outlined" size="small"
                    className={cls.btnRemoveRow}><IconDel /></Button>
                </Popconfirm>
              )}
            </div>
          ))}
          <div className={clsx(cls.row, cls.rowAccumulated)}>
            <div className={clsx(cls.col, cls.colBold)}>Accumulated</div>
            <div className={clsx(cls.col, cls.colBold)}>
              <Input row={accumulatedRow} field={'incomeTotal'} isFixed={true} />
            </div>
            <div className={clsx(cls.col, cls.colBold)}>
              <Input row={accumulatedRow} field={'profit'} isFixed={true} />
            </div>
          </div>
        </div>
      </div>
      {!isFixed && (
        <Button color="secondary" variant="outlined" size="small"
          onClick={addRow} className={cls.btnAddRow}><IconAdd /></Button>
      )}
    </div>
  );
};

interface InputProps {
  row: Row;
  field: Column;
  onChange?: any;
  index?: number;
  isFixed?: boolean;
  usedYears?: string[];
}

const shortYear = new Date().getFullYear() - 2000;
const yearsOptions: {value: string, label: string, disabled?: boolean}[] =
  Array.from(Array(shortYear + 1).keys()).reverse().map(year => ({
    value: (2000 + year).toString(),
    label: (2000 + year).toString(),
  }));

const Input = ({ row, field, onChange, index, isFixed, usedYears }: InputProps) => {
  const value = row[field];

  const yearsOptionsDisabled = yearsOptions.map(option => ({
    ...option,
    disabled: (usedYears || []).includes(option.value),
  }));

  return (
    field === 'year' ? (
      <Select
        defaultValue={value}
        bordered={false}
        style={{ width: 70, height: 28, color: '#042E6B', fontSize: 12 }}
        onChange={isFixed ? undefined : val => onChange(val, field, index)}
        disabled={isFixed}
        options={yearsOptionsDisabled}
        className={cls.selectInput}
      />
    ) : (
      <InputNumber
        min={0}
        max={1000000000}
        onBeforeInputCapture={e => {
          const value = e.currentTarget.value;
          if (value && value.includes('.')) {
            const [, second] = value.split('.');
            if (second.length >= 2) {
              e.stopPropagation();
              e.preventDefault();
            }
          }
        }}
        formatter={(val) => `$ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        parser={(val) => val ? +val.replace(/\$\s?|(,*)/g, '') : 0}
        value={value}
        bordered={false}
        onChange={val => onChange(val, field, index)}
        className={cls.input}
        disabled={isFixed}
        controls={false}
      />
    )
  );
};
